

import {
  ActivityEntry,
  ChecklistItem,
  CHECKLIST_TEMPLATE,
  Lead,
  Person,
  Proposal,
  PropertyRecord,
  SocialPost,
  StatusKey,
} from '../types';

export const SEED_PEOPLE: Person[] = [
  { id: 'faraz', name: 'Faraz Ahmed', role: 'principal', color: '#4b6bfb' },
  { id: 'michael', name: 'Michael Cole', role: 'principal', color: '#0ea5e9' },
  { id: 'simon', name: 'Simon Vega', role: 'principal', color: '#8b5cf6' },
  { id: 'dana', name: 'Dana Ruiz', role: 'broker', color: '#f59e0b' },
  { id: 'trevor', name: 'Trevor Lin', role: 'broker', color: '#10b981' },
  { id: 'nadia', name: 'Nadia Osei', role: 'broker', color: '#ef4444' },
  { id: 'priya', name: 'Priya Nair', role: 'marketing', color: '#ec4899' },
];

const iso = (daysAgo: number, hour = 10) => {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(hour, 0, 0, 0);
  return d.toISOString();
};

function template(overrides: Partial<Record<string, Partial<ChecklistItem>>> = {}): ChecklistItem[] {
  return CHECKLIST_TEMPLATE.map((t, i) => ({
    id: `ci_${t.key}_${i}`,
    key: t.key,
    label: t.label,
    hint: t.hint,
    status: 'not_started' as StatusKey,
    links: [],
    notes: [],
    ...(overrides[t.key] || {}),
  }));
}

export const SEED_PROPOSALS: Proposal[] = [
  {
    id: 'p1',
    property: 'Harborview Tower',
    address: '1200 Marina Blvd, San Diego, CA',
    assetType: 'Office',
    deckType: 'Proposal',
    requestedById: 'dana',
    requestedAt: iso(9),
    stage: 'ela_signed',
    checklist: template({
      deck: {
        status: 'done',
        updatedAt: iso(8),
        updatedById: 'faraz',
        links: [
          { id: 'l1', label: 'Harborview Proposal.pptx', url: 'https://dropbox.com/harborview-proposal', kind: 'dropbox', addedById: 'faraz', addedAt: iso(8) },
        ],
        notes: [{ id: 'n1', text: 'Client loved the repositioning angle — proceed.', authorId: 'faraz', createdAt: iso(8) }],
      },
      due_diligence: { status: 'done', updatedAt: iso(7), updatedById: 'michael' },
      war_room: { status: 'done', updatedAt: iso(7), updatedById: 'simon' },
      om: {
        status: 'done',
        updatedAt: iso(4),
        updatedById: 'simon',
        links: [{ id: 'l2', label: 'Harborview OM.pdf', url: 'https://dropbox.com/harborview-om', kind: 'dropbox', addedById: 'simon', addedAt: iso(4) }],
        notes: [{ id: 'n2', text: 'Sent Proposal to client for approval 7/22', authorId: 'faraz', createdAt: iso(4) }],
      },
      ela: {
        status: 'done',
        updatedAt: iso(2),
        updatedById: 'dana',
        notes: [{ id: 'n3', text: 'Signed ELA returned by client — cleared to list.', authorId: 'dana', createdAt: iso(2) }],
      },
      listing: { status: 'working', updatedAt: iso(1), updatedById: 'michael' },
      signs: { status: 'working', updatedAt: iso(1), updatedById: 'dana' },
      flyer: { status: 'not_started' },
      socials: { status: 'not_started' },
    }),
  },
  {
    id: 'p2',
    property: 'Cedar Logistics Park',
    address: '4500 Industry Way, Phoenix, AZ',
    assetType: 'Industrial',
    deckType: 'Pitch Deck',
    requestedById: 'trevor',
    requestedAt: iso(5),
    stage: 'in_progress',
    checklist: template({
      deck: {
        status: 'done',
        updatedAt: iso(4),
        updatedById: 'trevor',
        links: [{ id: 'l3', label: 'Cedar Pitch Deck', url: 'https://dropbox.com/cedar-pitch', kind: 'powerpoint', addedById: 'trevor', addedAt: iso(4) }],
      },
      due_diligence: { status: 'working', updatedAt: iso(2), updatedById: 'michael' },
      war_room: { status: 'working', updatedAt: iso(2), updatedById: 'simon' },
    }),
  },
  {
    id: 'p3',
    property: '5th & Main Retail',
    address: '501 Main St, Austin, TX',
    assetType: 'Retail',
    deckType: 'BOV',
    requestedById: 'dana',
    requestedAt: iso(1),
    stage: 'requested',
    checklist: template(),
  },
  {
    id: 'p4',
    property: 'Willow Creek Apartments',
    address: '88 Willow Creek Rd, Denver, CO',
    assetType: 'Multifamily',
    deckType: 'Proposal',
    requestedById: 'nadia',
    requestedAt: iso(12),
    stage: 'live',
    checklist: template({
      deck: { status: 'done' },
      due_diligence: { status: 'done' },
      war_room: { status: 'done' },
      om: { status: 'done' },
      ela: { status: 'done' },
      listing: { status: 'done', updatedAt: iso(6), updatedById: 'michael' },
      signs: { status: 'done' },
      flyer: { status: 'done' },
      socials: { status: 'working', updatedAt: iso(3), updatedById: 'priya' },
    }),
  },
];

export const SEED_PROPERTIES: PropertyRecord[] = [
  { id: 'pr1', name: 'Willow Creek Apartments', address: '88 Willow Creek Rd, Denver, CO', assetType: 'Multifamily', status: 'active', price: '$24.5M', brokerId: 'nadia', platforms: ['LoopNet', 'CoStar', 'Crexi', 'Company Website'], listedAt: iso(6) },
  { id: 'pr2', name: 'Harborview Tower', address: '1200 Marina Blvd, San Diego, CA', assetType: 'Office', status: 'active', price: '$61.0M', brokerId: 'dana', platforms: ['CoStar', 'Company Website'], listedAt: iso(1) },
  { id: 'pr3', name: 'Meridian Business Center', address: '210 Commerce Dr, Dallas, TX', assetType: 'Office', status: 'under_contract', price: '$38.2M', brokerId: 'trevor', platforms: ['LoopNet', 'CoStar', 'Crexi', 'Ten-X'], listedAt: iso(40) },
  { id: 'pr4', name: 'Sunset Storage Portfolio', address: 'Multiple — Southern CA', assetType: 'Self Storage', status: 'sold', price: '$52.9M', brokerId: 'nadia', platforms: ['CoStar', 'Crexi', 'Company Website', 'Brevitas'], listedAt: iso(120) },
  { id: 'pr5', name: 'Grove Street Medical', address: '77 Grove St, Portland, OR', assetType: 'Medical Office', status: 'sold', price: '$19.4M', brokerId: 'trevor', platforms: ['LoopNet', 'CoStar'], listedAt: iso(200) },
];

export const SEED_LEADS: Lead[] = [
  { id: 'ld1', contact: 'Robert Vance', company: 'Vance Capital Partners', interest: 'Office — value-add', status: 'qualified', ownerId: 'faraz', value: '$40M+', createdAt: iso(3) },
  { id: 'ld2', contact: 'Elena Marsh', company: 'Marsh Family Office', interest: 'Multifamily', status: 'contacted', ownerId: 'nadia', value: '$15–25M', createdAt: iso(6) },
  { id: 'ld3', contact: 'Owner — Beacon Ind.', company: 'Beacon Industrial LLC', interest: 'Disposition (industrial)', status: 'new', ownerId: 'trevor', value: '$30M', createdAt: iso(1) },
  { id: 'ld4', contact: 'Priyanka Shah', company: 'Everline REIT', interest: 'Retail portfolio', status: 'won', ownerId: 'dana', value: '$55M', createdAt: iso(20) },
  { id: 'ld5', contact: 'James Okafor', company: 'Okafor Holdings', interest: 'Self storage', status: 'lost', ownerId: 'nadia', value: '$12M', createdAt: iso(30) },
];

const day = (offset: number) => {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  d.setHours(9, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};

export const SEED_SOCIAL: SocialPost[] = [
  { id: 's1', proposalId: 'p4', title: 'Willow Creek — Launch Flyer', channel: 'flyer', date: day(-2), status: 'posted', ownerId: 'priya', example: 'Full-bleed hero of amenity deck with pricing teaser.', metrics: { impressions: 4200, engagements: 180, clicks: 96 } },
  { id: 's2', proposalId: 'p4', title: 'Willow Creek — Email Blast', channel: 'email', date: day(-1), status: 'posted', ownerId: 'priya', example: 'Sent to 3,400 investor contacts.', metrics: { impressions: 3400, engagements: 620, clicks: 210 } },
  { id: 's3', proposalId: 'p4', title: 'Willow Creek — LinkedIn Announcement', channel: 'linkedin', date: day(0), status: 'scheduled', ownerId: 'priya' },
  { id: 's4', proposalId: 'p4', title: 'Willow Creek — Instagram Reel', channel: 'instagram', date: day(2), status: 'planned', ownerId: 'priya' },
  { id: 's5', proposalId: 'p1', title: 'Harborview — Teaser Flyer', channel: 'flyer', date: day(3), status: 'planned', ownerId: 'priya' },
  { id: 's6', proposalId: 'p1', title: 'Harborview — LinkedIn', channel: 'linkedin', date: day(5), status: 'planned', ownerId: 'priya' },
];

export const SEED_ACTIVITY: ActivityEntry[] = [
  { id: 'a1', proposalId: 'p1', itemKey: 'om', authorId: 'faraz', text: 'noted on Offering Memorandum: “Sent Proposal to client for approval 7/22”', createdAt: iso(4) },
  { id: 'a2', proposalId: 'p1', itemKey: 'ela', authorId: 'dana', text: 'set “ELA (Exclusive Listing Agreement)” to Done', createdAt: iso(2) },
  { id: 'a3', proposalId: 'p3', authorId: 'dana', text: 'requested a BOV for 5th & Main Retail', createdAt: iso(1) },
  { id: 'a4', proposalId: 'p2', itemKey: 'due_diligence', authorId: 'michael', text: 'set “Due Diligence Items” to Working on it', createdAt: iso(2) },
];

